import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'

// Types
interface EmailMessage {
  messageId: string
  subject: string
  campaignName: string
  dateSent: string
  receivedDatetime: string | null
  openedDatetime: string | null
  received: boolean
  opened: boolean
  clicked: boolean
  events: any[]
}

interface EventData {
  id: string
  datetime: string
  properties: Record<string, any>
  uuid: string
}

// Styled Components
const Container = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const SectionTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`

const Section = styled.div`
  background: white;
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px ${theme.color.shadow.light};
`

const EmailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 600px;
  overflow-y: auto;
  padding-right: 1rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.color.background.light};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.color.border.light};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.color.text.grey};
  }
`

const EmailItem = styled.div`
  background: ${theme.color.background.lightGrey};
  border: 1px solid ${theme.color.border.light};
  border-radius: 6px;
  padding: 1.5rem;
`

const EmailSubject = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 0.5rem;
`

const EmailCampaign = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.grey};
  margin-bottom: 1rem;
`

const EmailDate = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.base};
  font-weight: 600;
  margin-bottom: 1rem;
`

const StatusRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const StatusItem = styled.div<{ checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.3rem;
  color: ${props => props.checked ? theme.color.accent.primary : theme.color.text.grey};
  font-weight: ${props => props.checked ? '600' : '400'};
`

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: default;
`

const StatusDateTime = styled.div`
  font-size: 1.2rem;
  color: ${theme.color.text.grey};
  margin-left: 2.6rem;
  margin-top: -0.25rem;
`

const MetricsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const MetricItem = styled.div`
  background: ${theme.color.background.lightGrey};
  border: 1px solid ${theme.color.border.light};
  border-radius: 6px;
  overflow: hidden;
`

const MetricHeader = styled.div`
  padding: 1rem 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;

  &:hover {
    background: ${theme.color.background.lightGrey};
  }
`

const MetricName = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
`

const MetricCount = styled.div`
  font-size: 1.2rem;
  color: ${theme.color.text.grey};
  background: ${theme.color.background.light};
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  position: relative;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${theme.color.border.light};
`

const ModalTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2.4rem;
  color: ${theme.color.text.grey};
  cursor: pointer;
  padding: 0;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${theme.color.text.base};
  }
`

const JsonDisplay = styled.pre`
  font-size: 1.2rem;
  font-family: 'Courier New', monospace;
  color: ${theme.color.text.base};
  padding: 1.5rem;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  background: ${theme.color.background.light};
  border-radius: 6px;
  border: 1px solid ${theme.color.border.light};
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-size: 1.4rem;
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  font-size: 1.4rem;
`

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-size: 1.4rem;
`

interface EmailCommsProps {
  userData: any
}

const EmailComms: React.FC<EmailCommsProps> = ({ userData }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [emails, setEmails] = useState<EmailMessage[]>([])
  const [events, setEvents] = useState<Record<string, EventData[]>>({})
  const [selectedMetric, setSelectedMetric] = useState<{ name: string; data: EventData[] } | null>(null)

  const userEmail = userData?.user?.email

  const fetchEmailData = async () => {
    setLoading(true)
    setError(null)

    try {
      // 1. Get profile by email
      const profileResponse = await fetch(`/api/klaviyo/profile?email=${encodeURIComponent(userEmail)}`)

      if (!profileResponse.ok) {
        if (profileResponse.status === 404) {
          throw new Error('Profile not found in Klaviyo')
        }
        throw new Error('Failed to fetch profile')
      }

      const profile = await profileResponse.json()
      const profileId = profile.id

      // 2. Fetch email events
      const emailEventsResponse = await fetch(`/api/klaviyo/email-events?profile_id=${profileId}`)

      if (!emailEventsResponse.ok) {
        throw new Error('Failed to fetch email events')
      }

      const emailData = await emailEventsResponse.json()
      setEmails(emailData.emails || [])

      // 3. Fetch all other events/metrics
      const allEventsResponse = await fetch(`/api/klaviyo/all-events?profile_id=${profileId}`)

      if (!allEventsResponse.ok) {
        throw new Error('Failed to fetch events')
      }

      const eventsData = await allEventsResponse.json()
      setEvents(eventsData.eventsByMetric || {})

    } catch (err: any) {
      console.error('Error fetching Klaviyo data:', err)
      setError(err.message || 'Failed to load email communications data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (userEmail) {
      fetchEmailData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail])

  const openMetricModal = (metricName: string, metricData: EventData[]) => {
    setSelectedMetric({ name: metricName, data: metricData })
  }

  const closeMetricModal = () => {
    setSelectedMetric(null)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading email communications...</LoadingMessage>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    )
  }

  return (
    <Container>
      <TwoColumnLayout>
        {/* Left Column: Emails */}
        <Section>
          <SectionTitle>Emails Sent</SectionTitle>
          {emails.length === 0 ? (
            <EmptyMessage>No emails found for this user</EmptyMessage>
          ) : (
            <EmailList>
              {emails.map((email) => (
                <EmailItem key={email.messageId}>
                  <EmailDate>Sent: {formatDate(email.dateSent)}</EmailDate>
                  <EmailSubject>{email.subject}</EmailSubject>
                  <EmailCampaign>{email.campaignName}</EmailCampaign>
                  <StatusRow>
                    <StatusItem checked={email.received}>
                      <Checkbox checked={email.received} disabled readOnly />
                      <span>Received</span>
                    </StatusItem>
                    {email.received && email.receivedDatetime && (
                      <StatusDateTime>{formatDate(email.receivedDatetime)}</StatusDateTime>
                    )}
                    <StatusItem checked={email.opened}>
                      <Checkbox checked={email.opened} disabled readOnly />
                      <span>Opened</span>
                    </StatusItem>
                    {email.opened && email.openedDatetime && (
                      <StatusDateTime>{formatDate(email.openedDatetime)}</StatusDateTime>
                    )}
                  </StatusRow>
                </EmailItem>
              ))}
            </EmailList>
          )}
        </Section>

        {/* Right Column: Events/Metrics */}
        <Section>
          <SectionTitle>Events / Metrics</SectionTitle>
          {Object.keys(events).length === 0 ? (
            <EmptyMessage>No events found for this user</EmptyMessage>
          ) : (
            <MetricsList>
              {Object.entries(events).map(([metricName, metricData]) => (
                <MetricItem key={metricName}>
                  <MetricHeader onClick={() => openMetricModal(metricName, metricData)}>
                    <MetricName>{metricName}</MetricName>
                    <MetricCount>{metricData.length} events</MetricCount>
                  </MetricHeader>
                </MetricItem>
              ))}
            </MetricsList>
          )}
        </Section>
      </TwoColumnLayout>

      {/* Modal for viewing JSON */}
      {selectedMetric && (
        <ModalOverlay onClick={closeMetricModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedMetric.name}</ModalTitle>
              <CloseButton onClick={closeMetricModal}>&times;</CloseButton>
            </ModalHeader>
            {selectedMetric.data.map((event: EventData, idx: number) => (
              <JsonDisplay key={event.id || idx}>
                {JSON.stringify({
                  datetime: event.datetime,
                  properties: event.properties
                }, null, 2)}
              </JsonDisplay>
            ))}
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  )
}

export default EmailComms
